<template>
  <div class="activity-feed">
    <div class="feed-tabs">
      <button
        v-for="t in tabs"
        :key="t.key"
        type="button"
        class="feed-tab"
        :class="{ active: activeTab === t.key }"
        @click="switchTab(t.key)"
      >
        {{ t.label }}
      </button>
    </div>

    <div v-if="loading && items.length === 0" class="feed-state">加载中…</div>

    <div v-else-if="error" class="feed-state">
      <div class="feed-error">{{ error }}</div>
      <button type="button" class="feed-btn" @click="reload">重试</button>
    </div>

    <div v-else-if="items.length === 0" class="feed-state">{{ emptyText }}</div>

    <div v-else class="feed-list">
      <div v-for="item in items" :key="itemKey(item)" class="feed-item">
        <div class="feed-rail">
          <span class="feed-dot" :style="{ background: dotColor(item) }"></span>
          <span class="feed-line"></span>
        </div>

        <div class="feed-main" :style="cardStyle(item)">
          <div class="feed-head">
            <span class="feed-tag">{{ metaOf(item).label }}</span>
            <span class="feed-time">{{ formatTime(item.createdAt) }}</span>
          </div>
          <div class="feed-title" v-if="titleText(item)">{{ titleText(item) }}</div>
          <div class="feed-desc">{{ descText(item) }}</div>
          <div v-if="cheerOf(item)" class="feed-cheer" :class="{ 'is-milestone': cheerOf(item).isMilestone }">{{ cheerOf(item).text }}</div>
          <div v-if="item.content && item.content !== '时间块'" class="feed-quote" :style="quoteStyle(item)">{{ item.content }}</div>
        </div>
      </div>

      <div v-if="hasMore" class="feed-more">
        <button type="button" class="feed-btn" :disabled="loading" @click="loadMore">
          {{ loading ? '加载中…' : '加载更多' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { listActivities, listSocialActivities } from '@/api/activity'
import { getCategoryStats } from '@/api/timeline'
import { toNumber } from '@/utils/number'
import { parseTagName, categoryPalette, hexToRgba } from '@/components/list/constants.js'

/**
 * 个人足迹动作展示模板（数据来自 user_activities 埋点表）。
 * 后端只返回结构化的 actionType / targetName，文案由前端持有，改一句话不用重新发版。
 */
const PERSONAL_META = {
  PROJECT_CREATE:    { label: '创建工作表', color: '#7aa3e0', verb: '创建了工作表' },
  TAG_CREATE:        { label: '添加类别',   color: '#c9a86a', verb: '添加了类别' },
  TIME_BLOCK_CREATE: { label: '创建时间块', color: '#8cc39f', verb: '创建了时间块' },
  TASK_COMPLETE:     { label: '完成任务',   color: '#639922', verb: '完成了任务' }
}

/**
 * 好友互动类型展示模板（数据由后端实时派生）。
 * OUT 表示我作用于好友的资源，IN 表示好友作用于我的资源。
 */
const SOCIAL_META = {
  // 评论系：橙 = 我发出、蓝 = 我收到；浅色是「评论」，深色是「回复」，灰色是「自己留言」
  COMMENT_OUT:       { label: '评论好友', color: '#f0a94c' },
  COMMENT_IN:        { label: '收到评论', color: '#6fa8dc' },
  COMMENT_REPLY_OUT: { label: '回复好友', color: '#d97b2c' },
  COMMENT_REPLY_IN:  { label: '收到回复', color: '#3d7cbf' },
  COMMENT_SELF:      { label: '自己留言', color: '#9aa5a0' },
  LIKE_OUT:          { label: '点赞好友', color: '#c9a86a' },
  LIKE_IN:           { label: '收到点赞', color: '#8cc39f' },
  FRIEND_ADD:        { label: '添加好友', color: '#639922' }
}

const FALLBACK_META = { label: '动态', color: '#94a59b', verb: '记录了一件事' }

const RESOURCE_LABEL = {
  WORKSPACE: '工作区',
  PROJECT: '工作表',
  TIMEBLOCK: '时间块'
}

const TABS = [
  { key: 'personal', label: '个人足迹' },
  { key: 'social', label: '好友互动' }
]

const EMPTY_TEXT = {
  personal: '还没有个人足迹，去创建工作表或完成一个任务试试',
  social: '还没有好友互动，给好友的时间块留个评论试试'
}

/**
 * 里程碑档位（小时）。累计时长跨过这些数字时值得庆祝一下。
 */
const MILESTONE_HOURS = [10, 25, 50, 100, 250, 500, 1000]

/**
 * 今日累计鼓励文案：{c} 类别名，{t} 今日时长。
 */
const TODAY_CHEERS = [
  '今天在「{c}」上专注了 {t}，这份投入很可以！',
  '「{c}」今天已经攒下 {t}，节奏不错，保持住～',
  '今天你在「{c}」里投入了 {t}，给自己点个赞。',
  '「{c}」今日累计 {t}，稳扎稳打，挺好的。'
]

/**
 * 兜底随机鼓励文案（无类别 / 无统计数据时用）。
 */
const RANDOM_CHEERS = [
  '每记一笔，都是在给未来的自己留个脚印。',
  '能一直记下来，本身就很了不起。',
  '时间都看在眼里，慢慢来，挺好的。',
  '小小一步，攒着攒着就是一大段路。',
  '把心思花在在意的事上，你已经做得很好了。'
]

/**
 * 关怀提示：按类别关键词 + 本次时长触发。
 * match 为类别名（去图标后的纯文本）包含的关键词。
 */
const CARE_RULES = [
  {
    match: ['吃饭', '用餐', '早餐', '午餐', '晚餐', '饮食', 'meal'],
    maxMinutes: 20,
    text: '「{c}」这次只留了 {d}，有点赶呀。慢点吃，给肠胃留足时间，身体会更舒服～'
  },
  {
    match: ['睡觉', '睡眠', '休息', '午睡', 'sleep'],
    maxMinutes: 360,
    text: '「{c}」只安排了 {d}，睡得偏少哦。有空多补点觉，精神才跟得上。'
  },
  {
    match: ['学习', '工作', '专注', '看书', '阅读', 'study', 'work'],
    minMinutes: 240,
    // 时长后紧跟助词「的」，此处刻意不留空格（数字与汉字之间保留空格，助词前不留）
    text: '连续 {d}的「{c}」很拼，起来走两步、看看远处，给眼睛和肩膀松松劲。'
  },
  {
    match: ['运动', '健身', '跑步', '锻炼', 'exercise', 'workout'],
    minMinutes: 90,
    text: '「{c}」做了 {d}，强度不小，记得量力而行、中间补补水～'
  },
  {
    match: ['通勤', '通学', '上下班', 'commute'],
    minMinutes: 45,
    text: '「{c}」路上就要 {d}，不短呀，注意安全，顺手听点喜欢的也挺好～'
  }
]

/**
 * 兼容后端多种时间格式：'2026-08-07T18:20:08'、'2026-08-07 18:20:08'、带毫秒或时区后缀。
 */
function parseTime(value) {
  if (!value) return null
  const normalized = String(value)
    .trim()
    .replace('T', ' ')
    .replace(/-/g, '/')
    .split('.')[0]
    .replace(/Z$/, '')
  const d = new Date(normalized)
  return isNaN(d.getTime()) ? null : d
}

/**
 * 分钟数转中文时长：「45 分钟」/「2 小时」/「1 小时 30 分钟」。
 */
function formatDuration(minutes) {
  const m = toNumber(minutes, 0)
  if (m <= 0) return ''
  const h = Math.floor(m / 60)
  const rest = m % 60
  if (h === 0) return `${rest} 分钟`
  if (rest === 0) return `${h} 小时`
  return `${h} 小时 ${rest} 分钟`
}

/**
 * 稳定哈希种子：同一条动态每次渲染必须拿到同一句文案，
 * 否则 Vue 重渲染 / 刷新页面时鼓励语会乱跳。
 */
function hashSeed(str) {
  let h = 0
  const s = String(str || '')
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0
  }
  return h
}

function pickOne(list, seed) {
  if (!list || !list.length) return ''
  return list[hashSeed(seed) % list.length]
}

function fillTemplate(text, values) {
  return String(text || '').replace(/\{(\w+)\}/g, (_, key) => {
    const v = values[key]
    return v == null ? '' : v
  })
}

/**
 * 类别名去掉 emoji / FA 图标前缀，只留纯文本（后端存的是 tags.name 原值）。
 */
function plainCategory(name) {
  return parseTagName(name).text || String(name || '')
}

export default {
  name: 'ActivityFeed',
  props: {
    // 每页条数
    size: { type: Number, default: 20 }
  },
  data() {
    return {
      tabs: TABS,
      activeTab: 'personal',
      items: [],
      loading: false,
      error: '',
      page: 1,
      total: 0,
      // 类别名（含 emoji 原值）-> { todayMinutes, totalMinutes }，供个人足迹文案取数
      categoryStats: {}
    }
  },
  computed: {
    hasMore() {
      return this.items.length > 0 && this.items.length < this.total
    },
    emptyText() {
      return EMPTY_TEXT[this.activeTab] || '还没有动态'
    }
  },
  created() {
    this.loadCategoryStats()
    this.fetch(true)
  },
  methods: {
    /**
     * 拉取按类别聚合的时长统计。
     *
     * 失败时静默降级为空统计——动态列表照常展示，只是少了累计类文案。
     */
    loadCategoryStats() {
      return getCategoryStats()
        .then(res => {
          const list = (res && res.code === 200 && Array.isArray(res.data)) ? res.data : []
          const map = {}
          for (const stat of list) {
            if (!stat || !stat.categoryName) continue
            map[stat.categoryName] = {
              todayMinutes: toNumber(stat.todayMinutes, 0),
              totalMinutes: toNumber(stat.totalMinutes, 0)
            }
          }
          this.categoryStats = map
        })
        .catch(() => {
          this.categoryStats = {}
        })
    },
    metaOf(item) {
      if (this.activeTab === 'social') {
        return SOCIAL_META[item.type] || FALLBACK_META
      }
      return PERSONAL_META[item.actionType] || FALLBACK_META
    },
    /**
     * 时间块动态（个人足迹）的主题色取自闭点时的类别色。
     * extra.categoryColor 由后端在 buildActivityExtra 写入（V15 起），
     * 历史动态经 V15 迁移 / backfill 脚本补齐；缺失时返回 null，
     * 调用方退回个人足迹默认绿，保证不白屏。
     *
     * 返回 { base, text }：base 用于填充圆点 / 卡片描边，text 是与底色
     * 满足 WCAG AA 对比度的可辨识文字色（交给 categoryPalette 派生）。
     */
    themeOf(item) {
      if (this.activeTab !== 'personal' || item.actionType !== 'TIME_BLOCK_CREATE') return null
      const extra = item.extra || {}
      const color = extra.categoryColor
      if (!color || typeof color !== 'string') return null
      const pal = categoryPalette(color, extra.categoryName)
      return { base: pal.base, text: pal.text }
    },
    dotColor(item) {
      const t = this.themeOf(item)
      return t ? t.base : this.metaOf(item).color
    },
    cardStyle(item) {
      const t = this.themeOf(item)
      // 整张卡片用类别色（无类别时用类型色）做半透明底色 + 同色淡描边，
      // 让「这一节属于哪个类别」一眼可见；圆点仍是实色类别色作点缀。
      const color = t ? t.base : this.metaOf(item).color
      return { background: hexToRgba(color, 0.10), borderColor: hexToRgba(color, 0.30) }
    },
    itemKey(item) {
      // 两类动态的源表不同，ID 可能撞号，加前缀隔离
      return `${this.activeTab}_${item.id}`
    },
    switchTab(key) {
      if (this.activeTab === key) return
      this.activeTab = key
      this.reload()
    },
    /**
     * 引用块的左侧色条跟随互动类型，让几种评论一眼能分开（个人足迹无类型色，退回默认绿）。
     */
    quoteStyle(item) {
      const color = this.metaOf(item).color
      return color ? { borderLeftColor: color } : {}
    },
    resourceText(item) {
      const label = RESOURCE_LABEL[item.resourceType] || '内容'
      return item.resourceName ? `${label}「${item.resourceName}」` : label
    },
    titleText(item) {
      if (this.activeTab !== 'social') {
        // 时间块动态的描述句已自带「时间块 XX」与类别，标题行冗余，省略避免重复
        if (item.actionType === 'TIME_BLOCK_CREATE') return ''
        const meta = this.metaOf(item)
        return `${meta.verb}「${item.targetName || ''}」`
      }
      const peer = item.peerName || '好友'
      switch (item.type) {
        case 'COMMENT_OUT':
          return `你评论了 ${peer} 的${this.resourceText(item)}`
        case 'COMMENT_IN':
          return `${peer} 评论了你的${this.resourceText(item)}`
        case 'COMMENT_REPLY_OUT':
          return `你回复了 ${peer}`
        case 'COMMENT_REPLY_IN':
          return `${peer} 回复了你`
        case 'COMMENT_SELF':
          return `你在自己的${this.resourceText(item)}下留了言`
        case 'LIKE_OUT':
          return `你赞了 ${peer} 的${this.resourceText(item)}`
        case 'LIKE_IN':
          return `${peer} 赞了你的${this.resourceText(item)}`
        case 'FRIEND_ADD':
          return `你和 ${peer} 成为了好友`
        default:
          return `你与 ${peer} 有了新的互动`
      }
    },
    descText(item) {
      if (this.activeTab === 'social') {
        // 时间块带上计划时段，让「哪一节时间块」一目了然
        if (item.resourcePeriod) return `时段：${item.resourcePeriod}`
        if (item.type === 'FRIEND_ADD') return '你们现在可以互相查看分享内容与动态。'
        // 回复类动态只写「你回复了谁」，资源上下文补在描述里
        if (item.type === 'COMMENT_REPLY_OUT' || item.type === 'COMMENT_REPLY_IN') {
          return `在${this.resourceText(item)}下`
        }
        if (item.type === 'COMMENT_SELF') return '这句话是留给你自己的。'
        return '来自好友分享内容的互动。'
      }
      return this.personalDesc(item)
    },
    /**
     * 个人足迹描述 = 主描述（带类别与时长）+ 最多一句附加鼓励。
     */
    personalDesc(item) {
      const extra = item.extra || {}
      const name = item.targetName || ''
      switch (item.actionType) {
        case 'PROJECT_CREATE':
          return `新建了一张计划管理表「${name}」，接下来可以在里面排任务啦。`
        case 'TAG_CREATE':
          return `新增了一个类别「${name}」，记录从此可以分门别类。`
        case 'TASK_COMPLETE':
          return `搞定了一项任务「${name}」，给自己鼓个掌！`
        case 'TIME_BLOCK_CREATE':
          return this.timeBlockDesc(item, extra)
        default:
          return '你记录了一件事'
      }
    },
    timeBlockDesc(item, extra) {
      const category = plainCategory(extra.categoryName)
      const rawName = item.targetName || ''
      // 后端对「没写事项」的时间块兜底成字面「时间块」，这里视作未命名，
      // 降级为不带事项的措辞，避免出现「时间块「时间块」」这种重复。
      const itemName = (rawName && rawName !== '时间块') ? rawName : ''
      const duration = formatDuration(extra.durationMinutes)
      // 时间块是「执行完才记录」的，所以描述只陈述归属与时长，不催执行；
      // 第一个「」紧跟「类别」、第二个紧跟「时间块」，层次一眼分清。
      let main
      if (category && itemName && duration) {
        main = `在「${category}」类别下记了时间块「${itemName}」，用时 ${duration}。`
      } else if (category && itemName) {
        main = `在「${category}」类别下记了时间块「${itemName}」。`
      } else if (category && duration) {
        main = `在「${category}」类别下记了一段时间，用时 ${duration}。`
      } else if (itemName && duration) {
        main = `记了时间块「${itemName}」，用时 ${duration}。`
      } else if (category) {
        main = `在「${category}」类别下记了一段时间。`
      } else if (itemName) {
        main = `记了时间块「${itemName}」。`
      } else {
        main = '在时间线上记了一段时间。'
      }
      // 附加鼓励句单独由 cheerOf 渲染（里程碑可上特殊颜色），此处只返回主句
      return main
    },
    timeBlockCheer(item, extra) {
      const category = plainCategory(extra.categoryName)
      const cheer = this.blockCheer(item, extra, category)
      return cheer ? { text: cheer.text, isMilestone: cheer.kind === 'milestone' } : null
    },
    cheerOf(item) {
      if (this.activeTab !== 'personal' || item.actionType !== 'TIME_BLOCK_CREATE') return null
      const extra = item.extra || {}
      return this.timeBlockCheer(item, extra)
    },
    /**
     * 附加句（鼓励 / 关怀 / 里程碑）：不再每次都播报里程碑进度，
     * 而是按条目稳定哈希从「可用候选」里择一呈现，让不同动态语气丰富、错落有致。
     * - 刚跨过里程碑档位是稀有时刻，固定庆祝（不参与轮换）；
     * - 其余候选：里程碑进度 / 今日累计鼓励 / 关怀提示 / 随机鼓励，按哈希择一。
     */
    blockCheer(item, extra, category) {
      // extra.categoryName 是后端存的原始值（可能带 emoji），统计表也以它为键
      const stat = category ? this.categoryStats[extra.categoryName] : null
      // 刚跨过里程碑档位是稀有时刻，固定庆祝，不参与轮换
      if (stat) {
        const celebrate = this.milestoneCelebrate(category, stat.totalMinutes)
        if (celebrate) return { text: celebrate, kind: 'milestone' }
      }
      const candidates = []
      if (stat) {
        const progress = this.milestoneProgress(category, stat.totalMinutes)
        if (progress) candidates.push({ text: progress, kind: 'milestone' })
        if (stat.todayMinutes >= 30) {
          candidates.push({
            text: fillTemplate(pickOne(TODAY_CHEERS, `${item.id}-today`), {
              c: category,
              t: formatDuration(stat.todayMinutes)
            }),
            kind: 'today'
          })
        }
      }
      const care = this.careText(category, toNumber(extra.durationMinutes, 0))
      if (care) candidates.push({ text: care, kind: 'care' })
      // 随机鼓励永远兜底，保证不空白
      candidates.push({ text: pickOne(RANDOM_CHEERS, `${item.id}-cheer`), kind: 'random' })
      // 同一动态用同一句（稳定哈希），不同动态自然错开，避免每次都重复同一文案
      const seed = hashSeed(`${item.id}-cheer`)
      return candidates[seed % candidates.length]
    },
    /**
     * 仅「刚跨过里程碑档位（不超过 10%）」时返回庆祝语，属稀有时刻。
     */
    milestoneCelebrate(category, totalMinutes) {
      const totalHours = toNumber(totalMinutes, 0) / 60
      const reached = [...MILESTONE_HOURS].reverse().find(m => totalHours >= m)
      if (!reached) return ''
      const next = MILESTONE_HOURS.find(m => m > reached)
      if (totalHours <= reached * 1.1) {
        return next
          ? `「${category}」类别累计已经达到 ${reached} 小时，下一个里程碑是 ${next} 小时——值得纪念一下！`
          : `「${category}」类别累计突破 ${reached} 小时，这数字相当厉害了，给自己鼓个掌！`
      }
      return ''
    },
    /**
     * 非稀有时刻的里程碑进度提示，仅作为候选之一（不再每次都播报）。
     */
    milestoneProgress(category, totalMinutes) {
      const totalHours = toNumber(totalMinutes, 0) / 60
      const reached = [...MILESTONE_HOURS].reverse().find(m => totalHours >= m)
      if (!reached) return ''
      const next = MILESTONE_HOURS.find(m => m > reached)
      if (totalHours <= reached * 1.1) return ''
      return next
        ? `「${category}」类别已累计 ${Math.floor(totalHours)} 小时，下一个里程碑是 ${next} 小时，慢慢来，稳住节奏～`
        : ''
    },
    careText(category, minutes) {
      if (!category || minutes <= 0) return ''
      for (const rule of CARE_RULES) {
        if (!rule.match.some(key => category.includes(key))) continue
        if (rule.maxMinutes != null && minutes > rule.maxMinutes) continue
        if (rule.minMinutes != null && minutes < rule.minMinutes) continue
        return fillTemplate(rule.text, { c: category, d: formatDuration(minutes) })
      }
      return ''
    },
    formatTime(value) {
      const d = parseTime(value)
      if (!d) return ''
      const pad = n => n.toString().padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} `
        + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    },
    reload() {
      this.page = 1
      this.fetch(true)
    },
    loadMore() {
      if (this.loading || !this.hasMore) return
      this.page += 1
      this.fetch(false)
    },
    fetch(reset) {
      this.loading = true
      if (reset) {
        this.error = ''
      }
      const params = { page: this.page, size: this.size }
      const api = this.activeTab === 'social' ? listSocialActivities : listActivities
      return api(params)
        .then(res => {
          const data = res && res.data ? res.data : null
          if (!res || res.code !== 200 || !data) {
            this.error = (res && res.message) || '加载动态失败'
            if (reset) this.items = []
            return
          }
          const list = Array.isArray(data.list) ? data.list : []
          this.items = reset ? list : this.items.concat(list)
          // 后端 Long 一律序列化成字符串，先转数字再比较
          this.total = toNumber(data.total, 0)
          this.error = ''
        })
        .catch(() => {
          this.error = '加载动态失败，请检查网络后重试'
          if (reset) this.items = []
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.activity-feed {
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  font-family: "Microsoft YaHei", sans-serif;
}

.activity-feed * {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Microsoft YaHei", sans-serif;
}

.feed-tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
  flex-shrink: 0;
}

.feed-tab {
  padding: 8px 18px;
  font-size: 14px;
  font-weight: 700;
  color: var(--lp-text-2, #5f7268);
  background: var(--lp-surface, #fff);
  border: 2px solid var(--lp-border, #e3efe6);
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.feed-tab:hover {
  background: var(--lp-primary-soft, #e9f5ec);
  border-color: var(--lp-primary-soft2, #d5ebdb);
  color: var(--lp-primary-dark, #2f7a4a);
}

.feed-tab.active {
  background: var(--lp-primary-soft, #e9f5ec);
  border-color: var(--lp-primary, #4aa46a);
  color: var(--lp-primary-dark, #2f7a4a);
}

.feed-list {
  display: flex;
  flex-direction: column;
}

.feed-item {
  display: flex;
  gap: 14px;
  align-items: stretch;
}

.feed-rail {
  position: relative;
  width: 12px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  padding-top: 18px;
}

.feed-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  z-index: 1;
}

.feed-line {
  position: absolute;
  top: 28px;
  bottom: -10px;
  width: 2px;
  background: var(--lp-border, #e3efe6);
  z-index: 0;
}

.feed-item:last-child .feed-line {
  display: none;
}

.feed-main {
  flex: 1;
  min-width: 0;
  padding: 12px 14px;
  margin-bottom: 12px;
  background: var(--lp-surface, #fff);
  border: 2px solid var(--lp-border, #e3efe6);
  border-radius: 0.75rem;
  box-shadow: var(--lp-shadow, 0 2px 10px rgba(52, 102, 68, .06));
  transition: box-shadow .18s ease;
}

.feed-main:hover {
  box-shadow: var(--lp-shadow-lg, 0 8px 24px rgba(52, 102, 68, .08));
}

.feed-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .5rem;
  margin-bottom: 6px;
}

.feed-tag {
  font-size: 12px;
  font-weight: 700;
  color: var(--lp-text-2, #5f7268);
}

.feed-time {
  font-size: 12px;
  color: var(--lp-text-3, #94a59b);
  font-weight: 600;
  flex-shrink: 0;
}

.feed-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--lp-text, #26332b);
  line-height: 1.5;
  word-break: break-word;
}

.feed-desc {
  font-size: 13px;
  color: var(--lp-text-2, #5f7268);
  margin-top: 4px;
  line-height: 1.6;
  word-break: break-word;
  white-space: pre-line;
  font-weight: 600;
}

.feed-cheer {
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.6;
  color: var(--lp-text-3, #94a59b);
  font-weight: 600;
  word-break: break-word;
}

.feed-cheer.is-milestone {
  color: #9c6b14;
  background: #fdf6e3;
  border-left: 3px solid #e0a93b;
  border-radius: 0 0.375rem 0.375rem 0;
  padding: 5px 10px;
  font-weight: 700;
}

.feed-quote {
  margin-top: 8px;
  padding: 8px 12px;
  font-size: 13px;
  line-height: 1.6;
  color: var(--lp-text, #26332b);
  background: var(--lp-surface-2, #f7fbf8);
  border-left: 3px solid var(--lp-primary, #4aa46a);
  border-radius: 0.375rem;
  word-break: break-word;
}

.feed-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
  color: var(--lp-text-3, #94a59b);
  font-size: 14px;
  font-weight: 600;
  padding: 24px 0;
}

.feed-error {
  color: var(--lp-text-2, #5f7268);
}

.feed-btn {
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
  color: var(--lp-primary-dark, #2f7a4a);
  background: var(--lp-primary-soft, #e9f5ec);
  border: 2px solid var(--lp-border, #e3efe6);
  border-radius: 0.625rem;
  cursor: pointer;
}

.feed-btn:hover {
  border-color: var(--lp-primary, #4aa46a);
}

.feed-btn:disabled {
  opacity: .6;
  cursor: default;
}

.feed-more {
  display: flex;
  justify-content: center;
  padding: 2px 0 6px;
}
</style>
